// src/components/AdminMenu.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, 
  Settings, 
  Package,
  Coffee,
  RefreshCw,
  Search,
  X,
  Save,
  Edit3,
  Plus,
  UserCheck,
  TrendingUp,
  BarChart3,
  Database,
  Table
} from 'lucide-react';
import VisitStats from './VisitStats';
import './AdminMenu.css';

const AdminMenu = ({ user, userProfile }) => {
  const navigate = useNavigate();
  const [selectedFeature, setSelectedFeature] = useState(null);
  const [adminData, setAdminData] = useState({
    users: [],
    stats: null,
    loading: false,
    error: null
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [editingUser, setEditingUser] = useState(null);
  const [newPoints, setNewPoints] = useState('');
  const [pointsNotes, setPointsNotes] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [recentVisitors, setRecentVisitors] = useState([]);
  
  // 데이터베이스 관리 관련 state
  const [selectedTable, setSelectedTable] = useState('');
  const [tableFields, setTableFields] = useState([]);
  const [tableRecords, setTableRecords] = useState([]);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [formType, setFormType] = useState(null);
  const [editRecord, setEditRecord] = useState({});
  const [sortField, setSortField] = useState('');
  const [sortDirection, setSortDirection] = useState('asc');
  const [dbSearchTerm, setDbSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [statusMessage, setStatusMessage] = useState('');

  // 관리자 권한 확인
  const isAdmin = user?.email === 'gategil1149@gmail.com' || user?.email === 'worldlandcafe@gmail.com';

  useEffect(() => {
    if (!isAdmin) {
      alert('관리자 권한이 필요합니다.');
      navigate('/');
    }
  }, [isAdmin, navigate]);

  // 검색어 변경 시 데이터 새로고침
  useEffect(() => {
    if (selectedFeature === 'cafe-points' && isAdmin) {
      const timeoutId = setTimeout(() => {
        fetchAdminData();
      }, 300);
      return () => clearTimeout(timeoutId);
    }
  }, [searchTerm]);

  const adminFeatures = [
    {
      icon: Coffee,
      title: 'CAFE Points 관리',
      description: '사용자들의 CAFE Points를 조회하고 관리할 수 있습니다.',
      onClick: () => {
        setSelectedFeature('cafe-points');
        fetchAdminData();
        fetchRecentVisitors();
      }
    },
    {
      icon: Database,
      title: '데이터베이스 관리',
      description: '데이터베이스 테이블을 조회하고 데이터를 관리할 수 있습니다.',
      onClick: () => {
        setSelectedFeature('database-manage');
      }
    },
    {
      icon: Package,
      title: 'NFT 데이터 관리',
      description: 'NFT 컬렉션과 메타데이터를 관리할 수 있습니다.',
      onClick: () => navigate('/nft-manage')
    }
  ];

  // 최근 방문자 데이터 가져오기
  const fetchRecentVisitors = async () => {
    if (!isAdmin) return;

    try {
      const serverUrl = process.env.REACT_APP_SERVER_URL || 'http://localhost:3002';
      const response = await fetch(`${serverUrl}/api/visit/recent-unique-visitors?limit=10`, {
        credentials: 'include'
      });

      const data = await response.json();

      if (data.success) {
        setRecentVisitors(data.visitors);
      } else {
        console.error('최근 방문자 로드 실패:', data.error);
        setRecentVisitors([]);
      }
    } catch (error) {
      console.error('최근 방문자 로드 실패:', error);
      setRecentVisitors([]);
    }
  };

  // 관리자 데이터 가져오기
  const fetchAdminData = async () => {
    if (!isAdmin) return;

    setAdminData(prev => ({ ...prev, loading: true, error: null }));
    
    try {
      const serverUrl = process.env.REACT_APP_SERVER_URL || 'http://localhost:3002';
      
      const [usersResponse, statsResponse] = await Promise.all([
        fetch(`${serverUrl}/api/admin/users?adminEmail=${encodeURIComponent(user.email)}&limit=100&search=${encodeURIComponent(searchTerm)}`, {
          credentials: 'include'
        }),
        fetch(`${serverUrl}/api/admin/cafepoints/stats?adminEmail=${encodeURIComponent(user.email)}`, {
          credentials: 'include'
        })
      ]);

      const [usersData, statsData] = await Promise.all([
        usersResponse.json(),
        statsResponse.json()
      ]);

      if (usersData.success && statsData.success) {
        setAdminData({
          users: usersData.users || [],
          stats: statsData.stats || null,
          loading: false,
          error: null
        });
      } else {
        throw new Error(usersData.error || statsData.error || '데이터 로드 실패');
      }

    } catch (error) {
      console.error('관리자 데이터 로드 실패:', error);
      setAdminData(prev => ({ 
        ...prev, 
        loading: false, 
        error: error.message 
      }));
    }
  };

  // 사용자 포인트 수정 시작
  const startEditingUser = (user) => {
    setEditingUser(user);
    setNewPoints(user.cafe_points.toString());
    setPointsNotes('');
  };

  // 사용자 포인트 수정 취소
  const cancelEditing = () => {
    setEditingUser(null);
    setNewPoints('');
    setPointsNotes('');
  };

  // 사용자 포인트 저장
  const saveUserPoints = async () => {
    if (!editingUser || !newPoints) return;

    setIsProcessing(true);
    try {
      const serverUrl = process.env.REACT_APP_SERVER_URL || 'http://localhost:3002';
      const response = await fetch(`${serverUrl}/api/admin/cafepoints/set`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          adminEmail: user.email,
          userEmail: editingUser.email,
          points: parseInt(newPoints),
          notes: pointsNotes
        })
      });

      const result = await response.json();
      
      if (result.success) {
        alert(result.message);
        cancelEditing();
        fetchAdminData();
      } else {
        alert(`오류: ${result.error}`);
      }

    } catch (error) {
      console.error('포인트 저장 실패:', error);
      alert('포인트 저장 중 오류가 발생했습니다.');
    } finally {
      setIsProcessing(false);
    }
  };

  // 사용자 포인트 추가
  // 사용자 포인트 추가
  const addUserPoints = async (userEmail) => {
    const points = prompt(`${userEmail}에게 추가할 포인트를 입력하세요:`, '10');
    if (!points || isNaN(parseInt(points))) return;

    const notes = prompt('메모 (선택사항):', '');

    try {
      const serverUrl = process.env.REACT_APP_SERVER_URL || 'http://localhost:3002';
      const response = await fetch(`${serverUrl}/api/admin/cafepoints/add`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          adminEmail: user.email,
          userEmail: userEmail,
          points: parseInt(points),
          notes: notes || ''
        })
      });

      const result = await response.json();
      
      if (result.success) {
        alert(result.message);
        fetchAdminData();
      } else {
        alert(`오류: ${result.error}`);
      }

    } catch (error) {
      console.error('포인트 추가 실패:', error);
      alert('포인트 추가 중 오류가 발생했습니다.');
    }
  };

  // ==================== 데이터베이스 관리 관련 함수들 ====================
  
  // 테이블 선택
  const handleTableSelect = async (tableName) => {
    setSelectedTable(tableName);
    setSelectedRecord(null);
    setFormType(null);
    setDbSearchTerm('');
    
    if (tableName) {
      await fetchTableData(tableName);
    } else {
      setTableFields([]);
      setTableRecords([]);
    }
  };

  // 테이블 데이터 가져오기
  const fetchTableData = async (tableName) => {
    setIsLoading(true);
    setStatusMessage('데이터를 불러오는 중...');
    
    try {
      const serverUrl = process.env.REACT_APP_SERVER_URL || 'http://localhost:3002';
      const response = await fetch(
        `${serverUrl}/api/admin/table/${tableName}?userEmail=${encodeURIComponent(user.email)}`,
        { credentials: 'include' }
      );
      
      const data = await response.json();
      
      if (data.success) {
        setTableFields(data.fields);
        setTableRecords(data.records);
        setStatusMessage('');
      } else {
        setStatusMessage(`오류: ${data.error}`);
      }
    } catch (error) {
      console.error('테이블 데이터 로드 실패:', error);
      setStatusMessage('데이터를 불러오는데 실패했습니다.');
    } finally {
      setIsLoading(false);
    }
  };

  // 레코드 추가
  const handleAddRecord = () => {
    setFormType('add');
    const newRecord = {};
    tableFields.forEach(field => {
      newRecord[field.Field] = '';
    });
    setEditRecord(newRecord);
  };

  // 레코드 수정
  const handleEditRecord = () => {
    if (!selectedRecord) {
      alert('수정할 레코드를 선택해주세요.');
      return;
    }
    setFormType('edit');
    setEditRecord({ ...selectedRecord });
  };

  // 레코드 삭제
  const handleDeleteRecord = async () => {
    if (!selectedRecord) {
      alert('삭제할 레코드를 선택해주세요.');
      return;
    }

    if (!window.confirm('정말 삭제하시겠습니까?')) return;

    setIsLoading(true);
    try {
      const serverUrl = process.env.REACT_APP_SERVER_URL || 'http://localhost:3002';
      const response = await fetch(
        `${serverUrl}/api/admin/table/${selectedTable}/record`,
        {
          method: 'DELETE',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            userEmail: user.email,
            id: selectedRecord.id
          })
        }
      );

      const result = await response.json();
      
      if (result.success) {
        alert('삭제되었습니다.');
        setSelectedRecord(null);
        await fetchTableData(selectedTable);
      } else {
        alert(`오류: ${result.error}`);
      }
    } catch (error) {
      console.error('삭제 실패:', error);
      alert('삭제 중 오류가 발생했습니다.');
    } finally {
      setIsLoading(false);
    }
  };

  // 레코드 저장
  const handleSaveRecord = async () => {
    setIsLoading(true);
    
    try {
      const serverUrl = process.env.REACT_APP_SERVER_URL || 'http://localhost:3002';
      const method = formType === 'add' ? 'POST' : 'PUT';
      const response = await fetch(
        `${serverUrl}/api/admin/table/${selectedTable}/record`,
        {
          method,
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            userEmail: user.email,
            record: editRecord
          })
        }
      );

      const result = await response.json();
      
      if (result.success) {
        alert(formType === 'add' ? '추가되었습니다.' : '수정되었습니다.');
        setFormType(null);
        setEditRecord({});
        await fetchTableData(selectedTable);
      } else {
        alert(`오류: ${result.error}`);
      }
    } catch (error) {
      console.error('저장 실패:', error);
      alert('저장 중 오류가 발생했습니다.');
    } finally {
      setIsLoading(false);
    }
  };

  // 정렬
  const handleSort = (fieldName) => {
    if (sortField === fieldName) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(fieldName);
      setSortDirection('asc');
    }
  };

  // 검색 필터링
  const getFilteredRecords = () => {
    let filtered = [...tableRecords];
    
    if (dbSearchTerm) {
      filtered = filtered.filter(record =>
        Object.values(record).some(value =>
          String(value).toLowerCase().includes(dbSearchTerm.toLowerCase())
        )
      );
    }

    if (sortField) {
      filtered.sort((a, b) => {
        const aVal = a[sortField];
        const bVal = b[sortField];
        
        if (aVal === null || aVal === undefined) return 1;
        if (bVal === null || bVal === undefined) return -1;
        
        if (sortDirection === 'asc') {
          return aVal > bVal ? 1 : -1;
        } else {
          return aVal < bVal ? 1 : -1;
        }
      });
    }

    return filtered;
  };

  // enum 값 파싱
  const parseEnumValues = (typeString) => {
    const match = typeString.match(/enum\((.*)\)/);
    if (!match) return [];
    return match[1].split(',').map(v => v.trim().replace(/'/g, ''));
  };

  // CAFE Points 관리 컴포넌트
  const renderCafePointsManagement = () => {
    return (
      <div className="feature-component">
        <div className="feature-header">
          <button onClick={() => setSelectedFeature(null)} className="back-btn">
            <ArrowLeft className="icon" />
            뒤로가기
          </button>
          <h2>CAFE Points 관리</h2>
        </div>

        <div className="cafe-management-content">
          {/* 통계 섹션 */}
          {adminData.stats && (
            <div className="admin-stats">
              <div className="stat-card">
                <div className="stat-icon">
                  <UserCheck className="icon" />
                </div>
                <div className="stat-info">
                  <span className="stat-number">{adminData.stats.total_users}</span>
                  <span className="stat-label">전체 사용자</span>
                </div>
              </div>
              <div className="stat-card">
                <div className="stat-icon">
                  <Coffee className="icon" />
                </div>
                <div className="stat-info">
                  <span className="stat-number">{adminData.stats.users_with_points}</span>
                  <span className="stat-label">포인트 보유자</span>
                </div>
              </div>
              <div className="stat-card">
                <div className="stat-icon">
                  <TrendingUp className="icon" />
                </div>
                <div className="stat-info">
                  <span className="stat-number">{adminData.stats.total_points}</span>
                  <span className="stat-label">총 포인트</span>
                </div>
              </div>
              <div className="stat-card">
                <div className="stat-icon">
                  <BarChart3 className="icon" />
                </div>
                <div className="stat-info">
                  <span className="stat-number">{Math.round(adminData.stats.avg_points)}</span>
                  <span className="stat-label">평균 포인트</span>
                </div>
              </div>
            </div>
          )}

          {/* 검색 섹션 */}
          <div className="admin-search">
            <div className="search-input-container">
              <Search className="search-icon" />
              <input
                type="text"
                placeholder="이메일, 닉네임 또는 이름으로 검색..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="search-input"
              />
            </div>

            {recentVisitors.length > 0 && (
              <select
                onChange={(e) => setSearchTerm(e.target.value)}
                value={searchTerm}
                className="search-input"
                style={{ flex: 1, minWidth: '150px', marginLeft: '1rem' }}
              >
                <option value="" disabled>최근 방문자 이메일 (10명)</option>
                {recentVisitors.map((visitor, index) => (
                  <option key={index} value={visitor.email}>
                    {visitor.email} ({new Date(visitor.last_visit).toLocaleDateString('ko-KR')})
                  </option>
                ))}
              </select>
            )}

            <button 
              onClick={fetchAdminData}
              className="refresh-btn"
              disabled={adminData.loading}
            >
              <RefreshCw className={`icon ${adminData.loading ? 'spinning' : ''}`} />
            </button>
          </div>

          {/* 사용자 목록 */}
          <div className="admin-users-section">
            {adminData.loading ? (
              <div className="admin-loading">
                <div className="spinner"></div>
                <span>사용자 목록을 불러오는 중...</span>
              </div>
            ) : adminData.error ? (
              <div className="admin-error">
                <p>오류: {adminData.error}</p>
                <button onClick={fetchAdminData} className="retry-btn">
                  다시 시도
                </button>
              </div>
            ) : (
              <div className="users-list">
                <div className="users-header">
                  <h3>사용자 목록 ({adminData.users.length}명)</h3>
                </div>
                
                <div className="users-table">
                  <div className="table-header">
                    <span>사용자</span>
                    <span>이메일</span>
                    <span>포인트</span>
                    <span>마지막 업데이트</span>
                    <span>액션</span>
                  </div>
                  
                  {adminData.users.map((userData, index) => (
                    <div key={index} className="table-row">
                      <div className="user-info">
                        <img 
                          src={userData.photo_url || '/default-avatar.png'} 
                          alt={userData.nickname}
                          className="user-avatar"
                          onError={(e) => {
                            e.target.src = '/default-avatar.png';
                          }}
                        />
                        <div className="user-details">
                          <span className="user-nickname">{userData.nickname}</span>
                          <span className="user-display-name">{userData.display_name}</span>
                        </div>
                      </div>
                      
                      <div className="user-email">{userData.email}</div>
                      
                      <div className="user-points">
                        {editingUser?.email === userData.email ? (
                          <div className="points-edit">
                            <input
                              type="number"
                              value={newPoints}
                              onChange={(e) => setNewPoints(e.target.value)}
                              className="points-input"
                              min="0"
                            />
                            <input
                              type="text"
                              value={pointsNotes}
                              onChange={(e) => setPointsNotes(e.target.value)}
                              placeholder="메모 (선택사항)"
                              className="notes-input"
                            />
                          </div>
                        ) : (
                          <span className={`points-value ${userData.cafe_points > 0 ? 'has-points' : 'no-points'}`}>
                            {userData.cafe_points.toLocaleString()}
                          </span>
                        )}
                      </div>
                      
                      <div className="user-updated">
                        {userData.points_updated_at 
                          ? new Date(userData.points_updated_at).toLocaleDateString('ko-KR')
                          : '-'
                        }
                      </div>
                      
                      <div className="user-actions">
                        {editingUser?.email === userData.email ? (
                          <div className="edit-actions">
                            <button 
                              onClick={saveUserPoints}
                              className="save-btn"
                              disabled={isProcessing}
                            >
                              <Save className="icon" />
                            </button>
                            <button 
                              onClick={cancelEditing}
                              className="cancel-btn"
                              disabled={isProcessing}
                            >
                              <X className="icon" />
                            </button>
                          </div>
                        ) : (
                          <div className="action-buttons">
                            <button 
                              onClick={() => startEditingUser(userData)}
                              className="edit-btn"
                              title="포인트 설정"
                            >
                              <Edit3 className="icon" />
                            </button>
                            <button 
                              onClick={() => addUserPoints(userData.email)}
                              className="add-btn"
                              title="포인트 추가"
                            >
                              <Plus className="icon" />
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  // 데이터베이스 관리 컴포넌트
  const renderDatabaseManagement = () => {
    return (
      <div className="feature-component">
        <div className="feature-header">
          <button onClick={() => setSelectedFeature(null)} className="back-btn">
            <ArrowLeft className="icon" />
            뒤로 가기
          </button>
          <h2>데이터베이스 관리</h2>
        </div>

        {/* 테이블 선택 */}
        <div className="table-selection-container">
          <select
            value={selectedTable}
            onChange={(e) => handleTableSelect(e.target.value)}
            className="table-select"
          >
            <option value="">-- 테이블 선택 --</option>
            <option value="users">사용자 (users)</option>
            <option value="cafepoints">CAFE Points</option>
            <option value="visit">페이지 방문 기록</option>
            <option value="NFT">NFT</option>
            <option value="chatboard">chatboard</option>
          </select>
        </div>

        {statusMessage && (
          <div className={`status-message ${isLoading ? 'loading' : ''}`}>
            {statusMessage}
            {isLoading && <div className="loading-spinner"></div>}
          </div>
        )}

        {selectedTable && (
          <>
            {/* 레코드 액션 버튼 */}
            <div className="record-control">
              <button 
                className="action-button add" 
                onClick={handleAddRecord}
                disabled={isLoading}
              >
                <Plus className="icon" /> 추가
              </button>
              
              <button 
                className="action-button edit" 
                onClick={handleEditRecord}
                disabled={isLoading || !selectedRecord}
              >
                <Edit3 className="icon" /> 수정
              </button>

              <button 
                className="action-button delete" 
                onClick={handleDeleteRecord}
                disabled={isLoading || !selectedRecord}
              >
                <X className="icon" /> 삭제
              </button>
              
              <div className="search-container">
                <Search className="search-icon" />
                <input
                  type="text"
                  placeholder="검색..."
                  value={dbSearchTerm}
                  onChange={(e) => setDbSearchTerm(e.target.value)}
                  className="search-input"
                />
              </div>
            </div>

            {/* 폼 (추가/수정) */}
            {formType && (
              <div className="form-container">
                <h3>{formType === 'add' ? '새 레코드 추가' : '레코드 수정'}</h3>
                <div className="field-inputs">
                  {tableFields.map((field) => (
                    <div key={field.Field} className="input-group">
                      <label>
                        {field.Field} {field.Null === 'NO' && <span className="required">*</span>}
                      </label>
                      {field.Type.startsWith('enum') ? (
                        <select
                          value={editRecord[field.Field] || ''}
                          onChange={(e) =>
                            setEditRecord({ ...editRecord, [field.Field]: e.target.value })
                          }
                        >
                          <option value="">선택하세요</option>
                          {parseEnumValues(field.Type).map((option) => (
                            <option key={option} value={option}>
                              {option}
                            </option>
                          ))}
                        </select>
                      ) : field.Type.includes('text') ? (
                        <textarea
                          value={editRecord[field.Field] || ''}
                          onChange={(e) =>
                            setEditRecord({ ...editRecord, [field.Field]: e.target.value })
                          }
                          rows="3"
                        />
                      ) : (
                        <input
                          type="text"
                          value={editRecord[field.Field] || ''}
                          onChange={(e) =>
                            setEditRecord({ ...editRecord, [field.Field]: e.target.value })
                          }
                        />
                      )}
                    </div>
                  ))}
                </div>
                <div className="form-buttons">
                  <button 
                    className="action-button save" 
                    onClick={handleSaveRecord}
                    disabled={isLoading}
                  >
                    {formType === 'add' ? '추가' : '저장'}
                  </button>
                  <button 
                    className="action-button cancel" 
                    onClick={() => {
                      setFormType(null);
                      setEditRecord({});
                    }}
                    disabled={isLoading}
                  >
                    취소
                  </button>
                </div>
              </div>
            )}

            {/* 레코드 목록 */}
            <div className="record-list-container">
              <h3>레코드 목록 ({getFilteredRecords().length}개)</h3>
              {getFilteredRecords().length > 0 ? (
                <div className="table-container">
                  <table className="record-table">
                    <thead>
                      <tr>
                        {tableFields.map((field) => (
                          <th 
                            key={field.Field} 
                            onClick={() => handleSort(field.Field)}
                            className={sortField === field.Field ? 'sorted' : ''}
                          >
                            {field.Field}
                            {sortField === field.Field && (
                              <span className="sort-icon">
                                {sortDirection === 'asc' ? '↑' : '↓'}
                              </span>
                            )}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {getFilteredRecords().map((record) => (
                        <tr
                          key={record.id}
                          className={selectedRecord?.id === record.id ? 'selected' : ''}
                          onClick={() => setSelectedRecord(record)}
                        >
                          {tableFields.map((field) => (
                            <td key={field.Field}>
                              {field.Type.includes('text') && record[field.Field] && record[field.Field].length > 100
                                ? record[field.Field].substring(0, 100) + '...'
                                : String(record[field.Field] || '')}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="no-data-message">
                  데이터가 없습니다.
                </div>
              )}
            </div>
          </>
        )}
      </div>
    );
  };

  if (selectedFeature === 'cafe-points') {
    return renderCafePointsManagement();
  }

  if (selectedFeature === 'database-manage') {
    return renderDatabaseManagement();
  }


  return (
    <div className="admin-menu">
      <div className="admin-menu-header">
        <button onClick={() => navigate('/')} className="back-btn">
          <ArrowLeft className="icon" />
          홈으로
        </button>
        <h1>관리자 메뉴</h1>
      </div>

      <VisitStats 
        pageName="admin-menu" 
        user={user}
        className="inline"
      />

      <div className="admin-features">
        <div className="features-header">
          <h2>관리 도구</h2>
          <p>WorldLand 관리자 전용 도구들입니다</p>
        </div>
        
        <div className="features-grid">
          {adminFeatures.map((feature, index) => (
            <div 
              key={index} 
              className="admin-feature-card clickable"
              onClick={feature.onClick}
            >
              <div className="feature-icon">
                <feature.icon className="icon" />
              </div>
              <h3 className="feature-title">{feature.title}</h3>
              <p className="feature-description">{feature.description}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default AdminMenu;